import React, { useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { UnifiedCitation } from '../../types/citation';
import { CitationHighlight } from './CitationHighlight';
import { CitationSuperscript } from './CitationSuperscript';
import { useRedactionMode } from '../../contexts/RedactionModeContext';
import { RedactionText } from '../redaction/RedactionText';
import { parsePiiMarkers } from '../../utils/parsePiiMarkers';

interface MarkdownWithCitationsProps {
  /** Markdown content that may contain [1], [2], etc. citation markers. */
  content: string;
  /** Citations array (1-indexed: [1] maps to citations[0]). */
  citations?: UnifiedCitation[];
  /** Starting number for citation markers. Defaults to 1. */
  startNumber?: number;
}

/**
 * Renders markdown content with inline [N] citation markers replaced
 * by interactive CitationMarker components.
 *
 * Shared component used by the Ask sidebar and other AI-powered views.
 */
export const MarkdownWithCitations: React.FC<MarkdownWithCitationsProps> = ({
  content,
  citations,
  startNumber = 1,
}) => {
  const { registerStructuredPii } = useRedactionMode();

  // Strip {{PII:TYPE}}...{{/PII}} marker syntax before markdown parsing.
  // This must happen first because remark-gfm autolinks emails, which splits
  // PII markers across DOM nodes and prevents RedactionText from matching them.
  const { cleanText: piiStrippedContent, spans: piiSpans } = parsePiiMarkers(content);

  // Extract PII values from markers and register them for blur matching.
  // Since markers are stripped before ReactMarkdown, downstream RedactionText
  // instances can no longer parse them — so we feed the values into the
  // structured PII set instead.
  const piiValues = useMemo(
    () => piiSpans.map(s => piiStrippedContent.substring(s.start, s.end)),
    [piiStrippedContent, piiSpans],
  );
  useEffect(() => {
    if (piiValues.length > 0) registerStructuredPii(piiValues);
  }, [piiValues, registerStructuredPii]);

  // These hooks must be called unconditionally (before any early return)
  // to satisfy the Rules of Hooks.
  const hasCitations = !!citations && citations.length > 0;

  const isSingleCitationRepeated = useMemo(() => {
    if (!hasCitations) return false;
    const allMarkers = [...piiStrippedContent.matchAll(/\[(\d+)\]/g)];
    if (allMarkers.length <= 1) return false;
    const uniqueNums = new Set(allMarkers.map(m => m[1]));
    return uniqueNums.size === 1;
  }, [piiStrippedContent, hasCitations]);

  const dotTracker = useMemo(() => {
    if (!isSingleCitationRepeated) return null;
    const paragraphs = piiStrippedContent.split(/\n\n/);
    const total = paragraphs.filter(p => /\[\d+\]/.test(p)).length;
    return { seen: 0, total };
  }, [piiStrippedContent, isSingleCitationRepeated]);
  // Reset counter each render
  if (dotTracker) dotTracker.seen = 0;

  if (!hasCitations) {
    // Strip [N] markers when no structured citations exist
    const cleanContent = piiStrippedContent.replace(/\[\d+\]/g, '');
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="mb-3 last:mb-0">{redactionWrapChildren(children)}</p>,
          ul: ({ children }) => <ul className="list-disc pl-5 mb-3">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-3">{children}</ol>,
          li: ({ children }) => <li className="mb-1">{redactionWrapChildren(children)}</li>,
          strong: ({ children }) => <strong className="font-semibold">{redactionWrapChildren(children)}</strong>,
          h3: ({ children }) => <h3 className="font-semibold text-base mt-4 mb-2">{redactionWrapChildren(children)}</h3>,
          h4: ({ children }) => <h4 className="font-semibold text-sm mt-3 mb-1">{redactionWrapChildren(children)}</h4>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-3">
              <table className="w-full text-sm border-collapse border border-gray-200 dark:border-gray-700">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-50 dark:bg-gray-800">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">{children}</th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">{children}</td>
          ),
        }}
      >
        {cleanContent}
      </ReactMarkdown>
    );
  }

  // Replace [N] markers with CitationMarker components
  const useGroupHover = isSingleCitationRepeated;
  const markdown = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => {
          let hideDots = false;
          if (dotTracker) {
            dotTracker.seen++;
            hideDots = dotTracker.seen < dotTracker.total;
          }
          return <p className="mb-3 last:mb-0">{processChildren(children, citations, startNumber, hideDots, useGroupHover)}</p>;
        },
        li: ({ children }) => {
          let hideDots = false;
          if (dotTracker) {
            dotTracker.seen++;
            hideDots = dotTracker.seen < dotTracker.total;
          }
          return <li className="mb-1">{processChildren(children, citations, startNumber, hideDots, useGroupHover)}</li>;
        },
        ul: ({ children }) => <ul className="list-disc pl-5 mb-3">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-5 mb-3">{children}</ol>,
        strong: ({ children }) => (
          <strong className="font-semibold">{processChildren(children, citations, startNumber)}</strong>
        ),
        h3: ({ children }) => (
          <h3 className="font-semibold text-base mt-4 mb-2">{processChildren(children, citations, startNumber)}</h3>
        ),
        h4: ({ children }) => (
          <h4 className="font-semibold text-sm mt-3 mb-1">{processChildren(children, citations, startNumber)}</h4>
        ),
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
            {processChildren(children, citations, startNumber)}
          </a>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-3">
            <table className="w-full text-sm border-collapse border border-gray-200 dark:border-gray-700">{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-gray-50 dark:bg-gray-800">{children}</thead>
        ),
        th: ({ children }) => (
          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
            {processChildren(children, citations, startNumber)}
          </th>
        ),
        td: ({ children }) => (
          <td className="px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
            {processChildren(children, citations, startNumber)}
          </td>
        ),
      }}
    >
      {piiStrippedContent}
    </ReactMarkdown>
  );

  // Wrap in a group container so all citation highlights respond to a single hover
  if (useGroupHover) {
    return <div className="group/cite">{markdown}</div>;
  }
  return markdown;
};

/**
 * Wrap string children in RedactionText for PII blurring (Tier 1 + Tier 2).
 * Non-string children pass through unchanged.
 */
function redactionWrapChildren(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string' && child.trim().length > 0) {
      return <RedactionText text={child} />;
    }
    return child;
  });
}

/**
 * Process React children recursively, replacing [N] text patterns
 * with CitationMarker components.
 */
function processChildren(
  children: React.ReactNode,
  citations: UnifiedCitation[],
  startNumber: number,
  hideDots?: boolean,
  useGroupHover?: boolean,
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return replaceMarkers(child, citations, startNumber, hideDots, useGroupHover);
    }
    if (React.isValidElement(child) && child.props?.children) {
      return React.cloneElement(child, {
        ...child.props,
        children: processChildren(child.props.children, citations, startNumber, hideDots, useGroupHover),
      });
    }
    return child;
  });
}

/**
 * Replace [N] text patterns with CitationHighlight components.
 * Wraps the text segment before each citation marker group in a dashed
 * underline with superscript numbers. Adjacent identical citation numbers
 * within a group are deduplicated to avoid double superscripts.
 */
function replaceMarkers(
  text: string,
  citations: UnifiedCitation[],
  startNumber: number,
  hideDots?: boolean,
  useGroupHover?: boolean,
): React.ReactNode {
  const regex = /\[(\d+)\]/g;

  // Collect all in-range [N] markers
  interface LiveMarker {
    number: number;
    citationIndex: number;
    startIndex: number;
    endIndex: number;
  }
  const liveMarkers: LiveMarker[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const num = parseInt(match[1], 10);
    const citIdx = num - startNumber;
    if (citIdx >= 0 && citIdx < citations.length) {
      liveMarkers.push({
        number: num,
        citationIndex: citIdx,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      });
    }
  }

  // No live markers — strip all [N] and return with PII redaction
  if (liveMarkers.length === 0) {
    const stripped = text.replace(/\[\d+\]/g, '');
    return <RedactionText text={stripped || text} />;
  }

  // Pass 3: group adjacent markers (no meaningful text between them)
  interface MarkerGroup {
    citations: Array<{ citation: UnifiedCitation; number: number }>;
    firstStartIndex: number;
    lastEndIndex: number;
  }
  const groups: MarkerGroup[] = [];
  let currentGroup: MarkerGroup | null = null;

  for (const marker of liveMarkers) {
    if (currentGroup === null) {
      currentGroup = {
        citations: [{ citation: citations[marker.citationIndex], number: marker.number }],
        firstStartIndex: marker.startIndex,
        lastEndIndex: marker.endIndex,
      };
    } else {
      const gap = text.substring(currentGroup.lastEndIndex, marker.startIndex);
      if (gap.trim() === '') {
        // Adjacent — merge into current group (deduplicate same number)
        if (!currentGroup.citations.some(c => c.number === marker.number)) {
          currentGroup.citations.push({ citation: citations[marker.citationIndex], number: marker.number });
        }
        currentGroup.lastEndIndex = marker.endIndex;
      } else {
        groups.push(currentGroup);
        currentGroup = {
          citations: [{ citation: citations[marker.citationIndex], number: marker.number }],
          firstStartIndex: marker.startIndex,
          lastEndIndex: marker.endIndex,
        };
      }
    }
  }
  if (currentGroup) groups.push(currentGroup);

  // Pass 3b: merge consecutive groups that cite the exact same documents.
  // This prevents repeated identical dots on adjacent sentences, e.g.
  // "...condition•. Aspirin...regimen•" → single highlight with one dot at the end.
  const mergedGroups: MarkerGroup[] = [];
  for (const group of groups) {
    const prev = mergedGroups[mergedGroups.length - 1];
    if (prev) {
      const prevKeys = prev.citations.map(c => c.number).sort().join(',');
      const curKeys = group.citations.map(c => c.number).sort().join(',');
      if (prevKeys === curKeys) {
        // Absorb this group into the previous one (keep its citations, extend range)
        prev.lastEndIndex = group.lastEndIndex;
        continue;
      }
    }
    mergedGroups.push({ ...group, citations: [...group.citations] });
  }

  // Pass 4: build output segments
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (let i = 0; i < mergedGroups.length; i++) {
    const group = mergedGroups[i];

    // Text from cursor up to (and including any intermediate text within) this group's markers.
    // For merged groups, this captures all text between the first and last marker, stripped of [N] markers.
    let segmentText = text.substring(cursor, group.lastEndIndex);
    segmentText = segmentText.replace(/\[\d+\]/g, '').replace(/\s+$/, '');

    if (segmentText.length > 0) {
      parts.push(
        <CitationHighlight
          key={`seg-${i}`}
          text={segmentText}
          citations={group.citations}
          hideDots={hideDots}
          useGroupHover={useGroupHover}
        />,
      );
    } else if (!hideDots) {
      // No text to underline — render standalone superscripts
      group.citations.forEach(({ citation, number }) => {
        parts.push(
          <CitationSuperscript key={`sup-${i}-${number}`} citation={citation} number={number} />,
        );
      });
    }

    cursor = group.lastEndIndex;
  }

  // Trailing text after last marker group — strip dead [N] markers
  if (cursor < text.length) {
    let trailing = text.substring(cursor).replace(/\[\d+\]/g, '');
    if (trailing) parts.push(<RedactionText key="trailing" text={trailing} />);
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}
